import { useEffect, useRef } from 'react'

import { makeStyles } from '@material-ui/core/styles'

import { ChartConfig } from '../../core/types/chat.types'

type ChartRendererProps = {
  config: ChartConfig
}

const useStyles = makeStyles(() => ({
  root: {
    position: 'relative',
    width: '100%',
    height: '100%',
    minHeight: 350,
  },
}))

const parseConfig = (config: ChartConfig): ChartConfig => {
  const configCopy = JSON.parse(JSON.stringify(config)) as ChartConfig

  const convertCallbacks = (obj: Record<string, unknown>) => {
    Object.keys(obj).forEach((key) => {
      const value = obj[key]

      if (typeof value === 'string' && value.trim().startsWith('function')) {
        try {
          const argsMatch = value.match(/function\s*\((.*?)\)/)
          const bodyMatch = value.match(/\{([\s\S]*)\}/)

          if (argsMatch?.[1] !== undefined && bodyMatch?.[1] !== undefined) {
            const args = argsMatch[1]
              .split(',')
              .map((arg) => arg.trim())
              .filter(Boolean)
            const body = bodyMatch[1].trim()
            const FunctionConstructor = Function
            obj[key] = FunctionConstructor(...args, body)
          }
        } catch (error) {
          console.error('Failed to parse chart callback function:', error)
        }
        return
      }

      if (value && typeof value === 'object' && !Array.isArray(value)) {
        convertCallbacks(value as Record<string, unknown>)
        return
      }

      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (entry && typeof entry === 'object' && !Array.isArray(entry)) {
            convertCallbacks(entry as Record<string, unknown>)
          }
        })
      }
    })
  }

  convertCallbacks(configCopy as unknown as Record<string, unknown>)
  return configCopy
}

const readCssVar = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') {
    return fallback
  }

  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

const applyDefaultStyles = (config: ChartConfig): ChartConfig => {
  const configCopy = JSON.parse(JSON.stringify(config)) as ChartConfig

  if (!configCopy.options) {
    configCopy.options = {}
  }

  const options = configCopy.options as Record<string, unknown>

  if (configCopy.type === 'bar') {
    options.responsive = true
    options.maintainAspectRatio = false
  }

  const chartTextColor = readCssVar('--ds-chart-text', 'rgba(31, 36, 48, 1)')
  const chartGridColor = readCssVar('--ds-chart-grid', 'rgba(31, 36, 48, 0.12)')

  if (!options.plugins) {
    options.plugins = {}
  }
  const plugins = options.plugins as Record<string, unknown>

  if (plugins.title && typeof plugins.title === 'object') {
    const title = plugins.title as Record<string, unknown>
    if (!title.color) {
      title.color = chartTextColor
    }
  }

  if (plugins.legend && typeof plugins.legend === 'object') {
    const legend = plugins.legend as Record<string, unknown>
    if (!legend.labels) {
      legend.labels = {}
    }

    const labels = legend.labels as Record<string, unknown>
    if (!labels.color) {
      labels.color = chartTextColor
    }
  }

  if (!options.scales) {
    options.scales = {}
  }
  const scales = options.scales as Record<string, unknown>

  Object.keys(scales).forEach((axisKey) => {
    const axis = scales[axisKey] as Record<string, unknown>

    if (axis.title && typeof axis.title === 'object') {
      const axisTitle = axis.title as Record<string, unknown>
      if (!axisTitle.color) {
        axisTitle.color = chartTextColor
      }
    }

    if (!axis.ticks) {
      axis.ticks = {}
    }
    const ticks = axis.ticks as Record<string, unknown>
    if (!ticks.color) {
      ticks.color = chartTextColor
    }
    if (ticks.backdropColor !== undefined || configCopy.type === 'radar') {
      ticks.backdropColor = 'transparent'
    }

    if (!axis.grid) {
      axis.grid = {}
    }
    const grid = axis.grid as Record<string, unknown>
    if (!grid.color) {
      grid.color = chartGridColor
    }

    if (configCopy.type === 'radar') {
      if (!axis.pointLabels) {
        axis.pointLabels = {}
      }
      const pointLabels = axis.pointLabels as Record<string, unknown>
      if (!pointLabels.color) {
        pointLabels.color = chartTextColor
      }
      if (!pointLabels.backdropColor) {
        pointLabels.backdropColor = 'transparent'
      }
    }
  })

  return configCopy
}

const ChartRenderer = ({ config }: ChartRendererProps) => {
  const classes = useStyles()
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const chartInstanceRef = useRef<{ destroy: () => void } | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return undefined
    }

    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy()
      chartInstanceRef.current = null
    }

    let isCancelled = false

    const renderChart = async () => {
      const chartModule = await import('chart.js')
      if (isCancelled) {
        return
      }

      const { Chart, registerables } = chartModule
      Chart.register(...registerables)

      // Apply Poppins to all Chart.js text (canvas doesn't inherit CSS font-family)
      Chart.defaults.font.family = "'Poppins', sans-serif"

      let parsedConfig = parseConfig(config)
      parsedConfig = applyDefaultStyles(parsedConfig)

      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }

      chartInstanceRef.current = new Chart(
        canvas,
        parsedConfig as never
      )
    }

    void renderChart()

    return () => {
      isCancelled = true
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy()
        chartInstanceRef.current = null
      }
    }
  }, [config])

  return (
    <div className={classes.root}>
      <canvas ref={canvasRef} />
    </div>
  )
}

export default ChartRenderer
